/**
 * Which document a CV-area screen reads, and which target its writes go to.
 *
 * The CV navigator serves three kinds of route from one layout:
 *   - the hub and the tailored subtree, which fetch their own documents;
 *   - the CV preview and its editors, which show the user's *primary* CV when
 *     no target is in the URL — that is the auto-apply `defaultCvId` fallback;
 *   - delegated routes (`/cv/access/<grantId>/…`), which carry an explicit
 *     route target and must write through to the CV owner.
 *
 * Job preferences are the exception that this module exists to encode: they
 * live on the master profile, and the API answers a CV-scoped write with
 * `CV_SCOPED_EDIT_UNSUPPORTED`. So the job-preferences editor must never
 * inherit the `defaultCvId` fallback — every save from a user who had picked a
 * primary CV failed — while an explicit delegated target still applies.
 *
 * Kept pure (no expo-router, no React) so the native and web layouts share one
 * rule and it can be unit-tested.
 */

/** Route (not `effectiveTarget`) forms that mean "my own master profile". */
export const MASTER_ROUTE_TARGET = 'master';

/** The job-preferences editor, in the normalized `usePathname()` form. */
export const CV_JOB_PREFERENCES_PATH = '/cv/edit-job-preferences';

/**
 * Whether a route target addresses a *profile*, as opposed to one CV document.
 *
 * `master` is the user's own profile and a `grant:`/`access:` target is a
 * delegation, which the API resolves to the owner's profile (and refuses on its
 * own terms when the grant is CV-scoped). Anything else is a CV id, which a
 * master-profile write must never be addressed to.
 */
export function isProfileScopedTarget(target?: string | null): boolean {
  if (!target) return true;
  return (
    target === MASTER_ROUTE_TARGET ||
    target.startsWith('grant:') ||
    target.startsWith('access:')
  );
}

export interface CvDocumentTargetInput {
  /** `usePathname()`, with or without a trailing slash. */
  pathname: string;
  /** Target parsed from the route (`cvTarget` param or `grant:<id>`). */
  routeTarget?: string | null;
  /** The auto-apply rule's primary CV, when one is configured. */
  defaultCvId?: string | null;
}

export interface CvDocumentTarget {
  /** True for routes that fetch their own document (hub, tailored subtree). */
  ownsDocument: boolean;
  /** True when a missing route target may fall back to the primary CV. */
  usesDefaultCvFallback: boolean;
  /** Target for reads and CV-scoped writes (layout, cv-config, sections). */
  documentTarget: string | undefined;
  /**
   * Target for profile-level writes (job preferences, open-to-work). Follows
   * the ROUTE target only, so the primary-CV fallback can never redirect a
   * master-profile write at a CV the API refuses to write to. `null` means the
   * signed-in user's own profile.
   */
  profileWriteTarget: string | null;
}

function normalizePath(pathname: string) {
  return pathname.replace(/\/$/, '');
}

function ownsOwnDocument(path: string) {
  return (
    path === '/cv/my-tabbio' ||
    path === '/cv/review' ||
    path === '/cv/tailored' ||
    path.startsWith('/cv/tailored/')
  );
}

export function resolveCvDocumentTarget({
  pathname,
  routeTarget,
  defaultCvId,
}: CvDocumentTargetInput): CvDocumentTarget {
  const path = normalizePath(pathname);
  const ownsDocument = ownsOwnDocument(path);
  const masterOnlyDocument = path === CV_JOB_PREFERENCES_PATH;
  const usesDefaultCvFallback = !ownsDocument && !masterOnlyDocument;

  // On the job-preferences editor a CV id is wrong however it arrived: as the
  // auto-apply fallback, or stamped explicitly by an entry point that carries
  // the CV being previewed (the setup sheet does exactly that). A CV-scoped
  // read answers `jobPreferences: null` and a CV-scoped write is refused, so
  // the editor showed an empty form and then failed to save. Only a
  // profile-scoped target — the user's own profile, or a delegation — survives.
  const effectiveRouteTarget =
    masterOnlyDocument && !isProfileScopedTarget(routeTarget)
      ? null
      : (routeTarget ?? null);

  const fallback = usesDefaultCvFallback ? defaultCvId : null;
  const documentTarget = ownsDocument
    ? undefined
    : (effectiveRouteTarget ?? fallback ?? undefined);

  return {
    ownsDocument,
    usesDefaultCvFallback,
    documentTarget,
    profileWriteTarget: effectiveRouteTarget,
  };
}

/**
 * Whether the profile query may run yet. Without a route target, a route that
 * uses the primary-CV fallback has to wait for the auto-apply overview, or the
 * first fetch would go out against the wrong document.
 */
export function isCvProfileQueryEnabled(input: {
  ownsDocument: boolean;
  usesDefaultCvFallback: boolean;
  routeTarget?: string | null;
  autoApplyIsLoading: boolean;
  autoApplyIsError: boolean;
}) {
  if (input.ownsDocument) return false;
  return (
    Boolean(input.routeTarget) ||
    !input.usesDefaultCvFallback ||
    !input.autoApplyIsLoading ||
    input.autoApplyIsError
  );
}
